'use client';

import { PressureData } from './DiagnosticFormInteractive';

interface StepPressurePointsProps {
  data: PressureData;
  onChange: (data: PressureData) => void;
}

const MAIN_CHALLENGES = [
  { id: 'financial', label: 'Gestão Financeira', description: 'Dificuldades com orçamentação, execução financeira ou prestação de contas' },
  { id: 'hr', label: 'Recursos Humanos', description: 'Gestão de pessoal, contratos, avaliações ou formação' },
  { id: 'procurement', label: 'Contratação Pública', description: 'Processos de aquisição, concursos ou ajustes diretos' },
  { id: 'legal', label: 'Apoio Jurídico', description: 'Questões legais, regulamentares ou de conformidade' },
  { id: 'admin', label: 'Organização Administrativa', description: 'Arquivo, processos internos ou gestão documental' },
  { id: 'funding', label: 'Candidaturas a Financiamento', description: 'Acesso a fundos europeus, nacionais ou programas de apoio' },
];

const OPERATIONAL_FREQUENCIES = [
  { id: 'deadlines', label: 'Gestão de Prazos', description: 'Dificuldade em cumprir prazos legais ou administrativos' },
  { id: 'overload', label: 'Sobrecarga da Equipa', description: 'A equipa está frequentemente sobrecarregada com tarefas' },
  { id: 'process', label: 'Organização de Processos', description: 'Falta de processos claros e documentados' },
  { id: 'technical', label: 'Dúvidas Técnicas', description: 'Necessidade frequente de consultar especialistas externos' },
  { id: 'specialized', label: 'Necessidade de Apoio Especializado', description: 'Tarefas que requerem competências não disponíveis internamente' },
  { id: 'reactive', label: 'Gestão Reativa', description: 'Tendência para resolver problemas apenas quando surgem' },
];

export default function StepPressurePoints({ data, onChange }: StepPressurePointsProps) {
  const toggleChallenge = (id: string) => {
    const updated = data.mainChallenges.includes(id)
      ? data.mainChallenges.filter((c) => c !== id)
      : [...data.mainChallenges, id];
    onChange({ ...data, mainChallenges: updated });
  };

  const toggleFrequency = (id: string) => {
    const updated = data.operationalFrequencies.includes(id)
      ? data.operationalFrequencies.filter((f) => f !== id)
      : [...data.operationalFrequencies, id];
    onChange({ ...data, operationalFrequencies: updated });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className="bg-white border border-gray-100 p-7"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-10 h-10 flex items-center justify-center flex-shrink-0"
            style={{ borderRadius: '5px', backgroundColor: '#E0F2F1', color: '#009688' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div>
            <h2
              className="text-gray-900 mb-1"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.4rem', fontWeight: 700 }}
            >
              Pontos de Pressão Operacional
            </h2>
            <p
              className="text-gray-500 text-sm leading-relaxed"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Selecione as áreas e situações que representam maiores desafios para a sua Junta de Freguesia. Pode selecionar múltiplas opções.
            </p>
          </div>
        </div>
      </div>

      {/* Main Challenges */}
      <div
        className="bg-white border border-gray-100 p-7"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <h3
          className="text-gray-900 font-semibold mb-1 flex items-center gap-2"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.05rem', fontWeight: 700 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
            <line x1="4" y1="22" x2="4" y2="15"/>
          </svg>
          Principais Áreas de Desafio
        </h3>
        <p className="text-gray-400 text-xs mb-5" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Quais as áreas que representam maiores dificuldades? <span className="text-error">*</span>
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {MAIN_CHALLENGES.map((challenge) => {
            const selected = data.mainChallenges.includes(challenge.id);
            return (
              <button
                key={challenge.id}
                type="button"
                onClick={() => toggleChallenge(challenge.id)}
                className={`flex items-start gap-3 p-4 text-left transition-all duration-200 min-h-[44px] ${
                  selected
                    ? 'border-2 border-primary' :'border-2 border-gray-100 hover:border-gray-300'
                }`}
                style={{
                  borderRadius: '5px',
                  backgroundColor: selected ? '#F0FAFA' : 'white',
                }}
                aria-pressed={selected}
              >
                <div
                  className={`w-8 h-8 flex items-center justify-center flex-shrink-0 transition-colors duration-200`}
                  style={{
                    borderRadius: '5px',
                    backgroundColor: selected ? '#009688' : '#F5F5F5',
                    color: selected ? 'white' : '#666',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="2" y="3" width="20" height="14" rx="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-semibold text-sm`}
                    style={{ fontFamily: 'DM Sans, sans-serif', color: selected ? '#009688' : '#1A1A1A' }}
                  >
                    {challenge.label}
                  </p>
                  <p
                    className="text-xs mt-0.5 leading-relaxed"
                    style={{ fontFamily: 'DM Sans, sans-serif', color: '#888' }}
                  >
                    {challenge.description}
                  </p>
                </div>
                <div
                  className={`w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-200`}
                  style={{
                    borderRadius: '3px',
                    backgroundColor: selected ? '#009688' : 'transparent',
                    borderColor: selected ? '#009688' : '#D0D0D0',
                  }}
                >
                  {selected && (
                    <svg width="8" height="7" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        {data.mainChallenges.length > 0 && (
          <p className="mt-3 text-xs font-medium" style={{ fontFamily: 'DM Sans, sans-serif', color: '#009688' }}>
            {data.mainChallenges.length} área(s) selecionada(s)
          </p>
        )}
      </div>

      {/* Operational Frequencies */}
      <div
        className="bg-white border border-gray-100 p-7"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <h3
          className="text-gray-900 font-semibold mb-1 flex items-center gap-2"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.05rem', fontWeight: 700 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Frequência de Desafios Operacionais
        </h3>
        <p className="text-gray-400 text-xs mb-5" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Quais destas situações ocorrem com frequência na sua organização? <span className="text-error">*</span>
        </p>
        <div className="space-y-2">
          {OPERATIONAL_FREQUENCIES.map((freq) => {
            const selected = data.operationalFrequencies.includes(freq.id);
            return (
              <button
                key={freq.id}
                type="button"
                onClick={() => toggleFrequency(freq.id)}
                className={`w-full flex items-center gap-4 p-4 text-left transition-all duration-200 min-h-[44px] ${
                  selected
                    ? 'border-2 border-primary' :'border-2 border-gray-100 hover:border-gray-300'
                }`}
                style={{
                  borderRadius: '5px',
                  backgroundColor: selected ? '#F0FAFA' : 'white',
                }}
                aria-pressed={selected}
              >
                <div
                  className={`w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200`}
                  style={{
                    borderRadius: '3px',
                    backgroundColor: selected ? '#009688' : 'transparent',
                    borderColor: selected ? '#009688' : '#D0D0D0',
                  }}
                >
                  {selected && (
                    <svg width="8" height="7" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <p
                    className={`font-semibold text-sm`}
                    style={{ fontFamily: 'DM Sans, sans-serif', color: selected ? '#009688' : '#1A1A1A' }}
                  >
                    {freq.label}
                  </p>
                  <p
                    className="text-xs mt-0.5"
                    style={{ fontFamily: 'DM Sans, sans-serif', color: '#888' }}
                  >
                    {freq.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
        {data.operationalFrequencies.length > 0 && (
          <p className="mt-3 text-xs font-medium" style={{ fontFamily: 'DM Sans, sans-serif', color: '#009688' }}>
            {data.operationalFrequencies.length} situação(ões) selecionada(s)
          </p>
        )}
      </div>
    </div>
  );
}